import React, { useCallback, useEffect, useState } from 'react';
import { PLATFORM_ADMIN_EMAIL } from '../constants/platformAdmin';
import { getSession } from '../services/authService';
import {
  getBillingSettings,
  loadCachedBillingSettings,
  setPaidPaymentEnabled,
  setPaymentMode,
} from '../services/billingSettingsService';
import {
  createPlatformTestAccount,
  resetPlatformTestAccount,
  PLATFORM_TEST_ACCOUNT_EMAIL,
} from '../services/adminTestAccountService';
import type { BillingPaymentMode, PlatformBillingSettings } from '../types/billingSettings';

function isAdminSessionEmail(email: string): boolean {
  return email.trim().toLowerCase() === PLATFORM_ADMIN_EMAIL;
}

const AdminPaymentOptionsPage: React.FC = () => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [settings, setSettings] = useState<PlatformBillingSettings>(() => loadCachedBillingSettings());
  const [loading, setLoading] = useState(true);
  const [savingPaid, setSavingPaid] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [testBusy, setTestBusy] = useState<'create' | 'reset' | null>(null);
  const [testMessage, setTestMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await getSession();
        const email = session?.user?.email ?? '';
        if (cancelled) return;
        setIsAdmin(isAdminSessionEmail(email));
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const fresh = await getBillingSettings();
      setSettings(fresh);
    } catch (err) {
      console.error(err);
      setError('결제 옵션을 불러오지 못했습니다. 마이그레이션 적용 여부를 확인해 주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      void reload();
    } else if (isAdmin === false) setLoading(false);
  }, [isAdmin, reload]);

  const handlePaidChange = async (enabled: boolean) => {
    if (savingPaid || settings.paid_payment_enabled === enabled) return;
    try {
      setSavingPaid(true);
      setError(null);
      const next = await setPaidPaymentEnabled(enabled);
      setSettings(next);
    } catch (err) {
      console.error(err);
      setError('유료 결제 활성화 저장에 실패했습니다.');
    } finally {
      setSavingPaid(false);
    }
  };

  const handleModeChange = async (mode: BillingPaymentMode) => {
    if (!settings.paid_payment_enabled || savingMode || settings.payment_mode === mode) return;
    try {
      setSavingMode(true);
      setError(null);
      const next = await setPaymentMode(mode);
      setSettings(next);
    } catch (err) {
      console.error(err);
      setError('PG 결제 모드 저장에 실패했습니다.');
    } finally {
      setSavingMode(false);
    }
  };

  const handleCreateTest = async () => {
    if (
      !window.confirm(
        `테스트 계정을 생성(또는 비밀번호 재발급)하시겠습니까?\n이메일: ${PLATFORM_TEST_ACCOUNT_EMAIL}\n플랜: 프로(메타)\n\n새 비밀번호는 완료 후 이 화면에 한 번만 표시됩니다.`,
      )
    ) {
      return;
    }
    try {
      setTestBusy('create');
      setTestMessage(null);
      const res = await createPlatformTestAccount();
      if (!res.ok) {
        setTestMessage(res.error || '테스트 계정 생성에 실패했습니다.');
        return;
      }
      setTestMessage(
        `테스트 계정 ${res.created ? '생성' : '갱신'} 완료: ${res.email} (프로) / 새 비밀번호: ${res.password ?? '-'} — 이 화면을 벗어나면 다시 볼 수 없습니다.`,
      );
    } catch (err) {
      console.error(err);
      setTestMessage('테스트 계정 생성 중 오류가 발생했습니다.');
    } finally {
      setTestBusy(null);
    }
  };

  const handleResetTest = async () => {
    if (
      !window.confirm(
        `${PLATFORM_TEST_ACCOUNT_EMAIL} 계정의 이용권·플랜을 초기화할까요?\n\n· plan → free\n· pass_epoch 증가(클라이언트 데모 이력 삭제)\n\n(로그인 중인 테스트 계정은 새로고침 후 반영됩니다.)`,
      )
    ) {
      return;
    }
    try {
      setTestBusy('reset');
      setTestMessage(null);
      const res = await resetPlatformTestAccount();
      if (!res.ok) {
        setTestMessage(res.error || '초기화에 실패했습니다.');
        return;
      }
      setTestMessage(`초기화 완료: ${res.email} → free (pass_epoch=${res.pass_epoch ?? '-'})`);
    } catch (err) {
      console.error(err);
      setTestMessage('테스트 계정 초기화 중 오류가 발생했습니다.');
    } finally {
      setTestBusy(null);
    }
  };

  if (isAdmin === null || (isAdmin && loading)) {
    return (
      <section className="cp-admin-notices-page cp-admin-notices-page--edu" aria-busy="true">
        <p className="cp-dashboard-placeholder" role="status">
          로딩 중…
        </p>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section className="cp-admin-notices-page cp-admin-notices-page--edu">
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자만 이용할 수 있습니다.
        </p>
      </section>
    );
  }

  const paidOn = settings.paid_payment_enabled;
  const mode = settings.payment_mode;

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-payment-page"
      aria-labelledby="cp-admin-payment-title"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-payment-title">결제 옵션 설정</h2>
          <p className="cp-admin-legal-subtitle">
            유료 플랜 노출·PG 모드·테스트 계정을 관리합니다. 변경은 즉시 서버에 저장되어 이용권 화면에 반영됩니다.
          </p>
        </div>
      </div>

      {error ? (
        <p className="cp-dashboard-placeholder" role="alert">
          {error}
        </p>
      ) : null}

      <div className="cp-admin-payment-banner" role="note">
        <h3 className="cp-admin-payment-banner-title">테스트 계정 로그인 안내</h3>
        <p className="cp-admin-payment-banner-body">
          이메일 <strong>{PLATFORM_TEST_ACCOUNT_EMAIL}</strong>
        </p>
        <p className="cp-admin-payment-banner-body">
          「테스트 계정 생성」을 누를 때마다 무작위 비밀번호가 새로 발급되어 아래에 한 번만 표시됩니다. 이용권 데모
          이력만 지우려면 「테스트 계정 이용권·플랜 초기화」를 사용하세요. 테스트 결제는 관리자 계정에서만 가능합니다.
        </p>
      </div>

      <div className="cp-admin-payment-cards">
        <article className="cp-dashboard-block cp-admin-payment-card">
          <h3 className="cp-admin-payment-card-title">유료 플랜 결제 활성화</h3>
          <p className="cp-admin-payment-card-desc">
            이용권 페이지에서 유료 플랜 구매를 활성화할지 선택하세요. 저장 시 서버에 반영되어 로그인한 사용자 이용권
            화면에 동기화됩니다.
          </p>
          <div className="cp-admin-payment-seg" role="group" aria-label="유료 결제 활성화">
            <button
              type="button"
              className={`cp-admin-payment-seg-btn${!paidOn ? ' is-active' : ''}`}
              disabled={savingPaid}
              onClick={() => void handlePaidChange(false)}
            >
              유료 결제 모드 잠금(베타 테스트)
            </button>
            <button
              type="button"
              className={`cp-admin-payment-seg-btn${paidOn ? ' is-active' : ''}`}
              disabled={savingPaid}
              onClick={() => void handlePaidChange(true)}
            >
              유료 결제 모드 활성화
            </button>
          </div>
          <div className="cp-admin-payment-help">
            {!paidOn ? (
              <span>
                유료 결제 모드 잠금: 유료·그룹 카드가 숨겨지고 「베타 테스트 이벤트 코드 요청」 카드가 표시됩니다.
                쿠폰 코드 등록은 가능합니다.
              </span>
            ) : (
              <span>유료 결제 모드 활성화: 유료 플랜 구매·업그레이드가 가능합니다.</span>
            )}
          </div>
        </article>

        <article
          className={`cp-dashboard-block cp-admin-payment-card${!paidOn ? ' is-disabled' : ''}`}
        >
          <h3 className="cp-admin-payment-card-title">PG 결제 모드 설정</h3>
          <p className="cp-admin-payment-card-desc">
            플랜 구매 시 결제 처리 방식을 선택하세요. 유료 결제 모드가 활성화된 경우에만 변경할 수 있습니다.
          </p>
          <div className="cp-admin-payment-seg" role="group" aria-label="PG 결제 모드">
            <button
              type="button"
              className={`cp-admin-payment-seg-btn${mode === 'test' ? ' is-active' : ''}`}
              disabled={!paidOn || savingMode}
              onClick={() => void handleModeChange('test')}
            >
              테스트 모드
            </button>
            <button
              type="button"
              className={`cp-admin-payment-seg-btn${mode === 'production' ? ' is-active' : ''}`}
              disabled={!paidOn || savingMode}
              onClick={() => void handleModeChange('production')}
            >
              실제 결제 모드
            </button>
          </div>
          <div className="cp-admin-payment-help">
            {!paidOn ? (
              <span>유료 결제 모드를 활성화한 뒤 PG 모드를 선택할 수 있습니다.</span>
            ) : mode === 'test' ? (
              <span>
                테스트 모드: 관리자 계정만 결제하기 시 테스트 비밀번호 확인 후 실제 PG 없이 데모 이용권이
                반영됩니다(서버에서도 관리자만 허용).
              </span>
            ) : (
              <span>
                실제 결제 모드: 실 PG 연동 전까지 결제가 차단되며 안내만 표시됩니다. PortOne 등 연동 후 이 모드에서
                실결제가 진행됩니다.
              </span>
            )}
          </div>
        </article>

        <article className="cp-dashboard-block cp-admin-payment-card">
          <h3 className="cp-admin-payment-card-title">테스트 계정</h3>
          <p className="cp-admin-payment-card-desc">
            생성 시 프로 플랜 메타로 맞추고, 이미 있으면 비밀번호를 무작위로 재발급합니다. 새 비밀번호는 완료 메시지에
            한 번만 표시됩니다.
          </p>
          <div className="cp-admin-payment-actions">
            <button
              type="button"
              className="cp-btn-secondary cp-admin-payment-action-btn"
              disabled={testBusy != null}
              onClick={() => void handleCreateTest()}
            >
              {testBusy === 'create' ? '처리 중…' : '테스트 계정 생성'}
            </button>
            <button
              type="button"
              className="cp-btn-secondary cp-admin-payment-action-btn"
              disabled={testBusy != null}
              onClick={() => void handleResetTest()}
            >
              {testBusy === 'reset' ? '처리 중…' : '테스트 계정 이용권·플랜 초기화'}
            </button>
          </div>
          {testMessage ? (
            <p className="cp-admin-payment-test-msg" role="status">
              {testMessage}
            </p>
          ) : null}
        </article>
      </div>
    </section>
  );
};

export default AdminPaymentOptionsPage;
